import { useReducer } from "react";
import ZoneContext, { internalState } from "./context";
import reducer from "./reducer";
import { execute } from "../../../utils/helpers/execute";
import EndPoints from "../../../services/end-points";
import { IZone, IZoneDetails } from "../../../models/wms/zone/response";
import { ICreateZone, IUpdateZone } from "../../../models/wms/zone/request";
import { IZoneQuery } from "../../../models/wms/zone/query";

export interface IProps {
  children: React.ReactNode;
}

const ZoneContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.wmsLayout.getAllZones(state.query);

        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDetails = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });

        const { data } = await EndPoints.wmsLayout.getZone(id);
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
      },
      throwException: false,
    });
  };

  const setDetails = async (data?: IZoneDetails) => {
    dispatch({ type: "SET_DETAILS", payload: { details: data } });
  };

  const createZone = async (request: ICreateZone) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });

        await EndPoints.wmsLayout.createZone(request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });
      },
      throwException: true,
    });
  };

  const updateZone = async (id: number, request: IUpdateZone) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.updateZone(id, request);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const deleteZone = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });

        await EndPoints.wmsLayout.deleteZone(id);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
      },
      throwException: true,
    });
  };

  const reorderZone = async (id: number, pickSequence: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.reorderZone(id, pickSequence);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const toggleZoneActive = async (id: number, isActive: boolean) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.toggleZoneActive(id, isActive);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IZoneQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  return (
    <ZoneContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getDetails,
          setDetails,
          createZone,
          updateZone,
          deleteZone,
          reorderZone,
          toggleZoneActive,
          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </ZoneContext.Provider>
  );
};

export default ZoneContextProvider;
